// Funções puras do módulo "Templates Meta por conexão".
// Sem dependência de React/axios de propósito: tudo aqui é testável com jest
// e reutilizável tanto pelo MetaWabaSnapshot quanto pelos dialogs do módulo.

import { translateMetaError } from './metaErrors'

// Provider da conexão Meta Oficial (Cloud API). Só conexões com esse provider
// podem ver/usar as ações de template — Baileys nunca passa por aqui.
export const META_OFFICIAL_PROVIDER = 'hiperapi-v3-cloud-official'

export function isMetaOfficialProvider(provider) {
  return provider === META_OFFICIAL_PROVIDER
}

// Paleta usada pelos chips/pills de status (mesmos tons do snapshot da WABA).
export const STATUS_COLORS = {
  green: '#22c55e',
  yellow: '#eab308',
  red: '#ef4444',
  gray: '#94a3b8',
  blue: '#3b82f6',
  orange: '#f97316',
}

// Categorias aceitas pela Meta, com descrição curta pra UI de criação.
export const TEMPLATE_CATEGORIES = [
  {
    value: 'UTILITY',
    label: 'Utilitário',
    description: 'Avisos transacionais: cobranças, confirmações, atualizações de pedido. Aprovação mais fácil.',
  },
  {
    value: 'MARKETING',
    label: 'Marketing',
    description: 'Promoções, ofertas e novidades. Exige opt-in do cliente e afeta mais a qualidade do número.',
  },
  {
    value: 'AUTHENTICATION',
    label: 'Autenticação',
    description: 'Códigos de verificação (OTP). Formato restrito definido pela Meta.',
  },
]

// Rótulos pt-br por extenso de cada categoria — usados no snapshot da WABA
// (dialog de detalhe e tabela de modelos) e agora também na coluna Modelo dos
// relatórios (tooltip do badge). OTP entra à parte de AUTHENTICATION porque a
// Meta já devolveu essa categoria separada em templates reais deste sistema.
export const TEMPLATE_CATEGORY_LABELS = {
  MARKETING: 'Marketing',
  UTILITY: 'Utilitário',
  AUTHENTICATION: 'Autenticação',
  OTP: 'OTP',
}

// Forma curta pra coluna de tabela — o rótulo por extenso vive no tooltip.
export const TEMPLATE_CATEGORY_SHORT = {
  MARKETING: 'MKT',
  UTILITY: 'UTIL',
  AUTHENTICATION: 'AUTH',
  OTP: 'OTP',
}

const VALID_CATEGORIES = TEMPLATE_CATEGORIES.map((c) => c.value)

// Chave de casamento entre registro local e template do snapshot: nome + idioma
// em minúsculas (a Meta trata nome como case-insensitive na prática).
function templateKey(name, language) {
  return `${String(name || '').trim().toLowerCase()}::${String(language || '').trim().toLowerCase()}`
}

/**
 * Une os registros persistidos (POST /list) com os templates do snapshot da
 * WABA, casando por nome+idioma minúsculos. Prefere os dados do registro e
 * completa com o snapshot (ex.: components ainda não sincronizados). Itens que
 * só existem no snapshot viram linhas "somente Meta" (sem _id local).
 * Retorna ordenado por nome (e idioma como desempate).
 */
export function mergeTemplates(records, snapshotTemplates) {
  const safeRecords = Array.isArray(records) ? records : []
  const safeSnapshot = Array.isArray(snapshotTemplates) ? snapshotTemplates : []

  const snapshotByKey = new Map()
  for (const t of safeSnapshot) {
    const key = templateKey(t?.name, t?.language)
    if (!snapshotByKey.has(key)) snapshotByKey.set(key, t)
  }

  const usedKeys = new Set()
  const merged = []

  for (const record of safeRecords) {
    const key = templateKey(record?.metaTemplateName, record?.language)
    const snap = snapshotByKey.get(key) || null
    usedKeys.add(key)
    merged.push({
      key,
      name: record?.metaTemplateName || snap?.name || '',
      language: record?.language || snap?.language || '',
      category: record?.category || snap?.category || null,
      // status exibido: o do registro (reconciliado) vence; snapshot completa
      status: record?.metaReviewStatus || snap?.status || null,
      components: Array.isArray(record?.components) && record.components.length > 0
        ? record.components
        : (Array.isArray(snap?.components) ? snap.components : []),
      _id: record?._id || null,
      record,
      metaReviewStatus: record?.metaReviewStatus || null,
      localProcessingStatus: record?.localProcessingStatus || null,
      rejectionReason: record?.rejectionReason || null,
      lastError: record?.lastError || null,
      lastSyncAt: record?.lastSyncAt || null,
      origin: record?.origin || null,
      // A conta em que este registro nasceu — é o que permite à tela separar o
      // que é da conexão de hoje do que sobrou de uma conta anterior.
      wabaId: record?.wabaId || null,
      onlyMeta: false,
    })
  }

  for (const t of safeSnapshot) {
    const key = templateKey(t?.name, t?.language)
    if (usedKeys.has(key)) continue
    usedKeys.add(key)
    merged.push({
      key,
      name: t?.name || '',
      language: t?.language || '',
      category: t?.category || null,
      status: t?.status || null,
      components: Array.isArray(t?.components) ? t.components : [],
      _id: null,
      record: null,
      metaReviewStatus: null,
      localProcessingStatus: null,
      rejectionReason: null,
      lastError: null,
      lastSyncAt: null,
      origin: null,
      onlyMeta: true,
    })
  }

  return merged.sort((a, b) =>
    a.name.localeCompare(b.name) || a.language.localeCompare(b.language)
  )
}

// Pode reenviar pra Meta? Quando o envio local falhou, quando a Meta rejeitou,
// ou quando o template não existe mais lá (NOT_FOUND) — este último é o
// "recriar na Meta": alguém apagou no gerenciador, ou o registro nasceu numa
// WABA da qual a conexão saiu. Sem ele o registro virava beco sem saída na
// tela: não dá pra editar (não há o que editar na Graph) e o dialog de
// recomendados não o oferece de volta, então a única saída era apagar e
// recriar, perdendo o histórico.
// Aceita tanto o registro cru quanto a linha mesclada (ambos carregam os campos).
// Lê a resposta de `metaTemplates.list` e devolve só o que a Meta aprovou.
//
// Existe como função porque a leitura já foi feita errada duas vezes no mesmo
// lugar, e cada engano sozinho zerava a lista:
//   - o endpoint responde { waba, templates } — quem procurava `items` ou
//     `data` recebia sempre vazio;
//   - o status do registro persistido é `metaReviewStatus`; `status` só existe
//     nos itens já mesclados com o retrato da Meta (ver mergeTemplates).
// Aceitar os dois campos deixa a função servir para registro cru e para item
// mesclado, que é como as telas consomem isso hoje.
export function templatesAprovados(resposta) {
  const lista = Array.isArray(resposta?.templates)
    ? resposta.templates
    : Array.isArray(resposta)
      ? resposta
      : []

  return lista.filter((t) => {
    const status = String(t?.metaReviewStatus || t?.status || '').toUpperCase().trim()
    return status === 'APPROVED'
  })
}

// O nome do modelo muda de campo conforme a origem: registro persistido usa
// `metaTemplateName`, item vindo do retrato da Meta usa `name`.
export function nomeDoTemplate(template) {
  return String(template?.name || template?.metaTemplateName || '').trim()
}

// O envio pela API ainda sai sempre em pt_BR: o snippet de meta-tags que o
// sistema do cliente manda só carrega o NOME do modelo, e o secondary completa
// o idioma com pt_BR fixo. Um modelo em inglês seria aprovado pela Meta e
// falharia em todo envio ("modelo não existe nesse idioma"). Até o envio
// respeitar o idioma gravado, o painel só cria modelos em pt_BR e avisa quando
// um modelo que já existe está em outro idioma.
export const IDIOMA_DO_ENVIO = 'pt_BR'

const NOMES_DOS_IDIOMAS = {
  pt_BR: 'Português (Brasil)',
  pt_PT: 'Português (Portugal)',
  en_US: 'Inglês (EUA)',
  es_ES: 'Espanhol (Espanha)',
  es_MX: 'Espanhol (México)',
}

// Código desconhecido aparece como veio (ex.: "fr"), melhor que sumir.
export function nomeDoIdioma(codigo) {
  const c = String(codigo || '').trim()
  return NOMES_DOS_IDIOMAS[c] || c
}

// O modelo sai no envio de hoje? Sem idioma informado conta como pt_BR: é o
// que o envio usa, e esconder um modelo por falta de dado seria pior.
export function idiomaSaiNoEnvio(template) {
  const idioma = String(template?.language || '').trim()
  return !idioma || idioma === IDIOMA_DO_ENVIO
}

export function canRetry(record) {
  if (!record) return false
  return (
    record.localProcessingStatus === 'failed' ||
    record.metaReviewStatus === 'REJECTED' ||
    record.metaReviewStatus === 'NOT_FOUND'
  )
}

// O registro é de OUTRA conta do WhatsApp Business? Acontece quando a conexão
// troca de WABA (Embedded Signup refeito noutra conta): os registros da conta
// antiga continuam pendurados na instância e o sync os marca NOT_FOUND, porque
// olha a conta atual. Sem essa distinção a tela mostra uma lista que mistura as
// duas e ninguém entende por que metade está "não encontrado na Meta".
export function isFromAnotherWaba(record, currentWabaId) {
  if (!record || !record.wabaId || !currentWabaId) return false
  return String(record.wabaId) !== String(currentWabaId)
}

/**
 * Deve avisar "outra conta" nesta linha?
 *
 * Não basta o registro ser de outra WABA: ele também precisa NÃO existir na
 * conta atual. A distinção é o que separa um aviso útil de um aviso perigoso.
 * Houve um estado em que a sincronização casava registro e template só por
 * nome+idioma, ignorando a conta — e o registro da conta antiga acabava
 * carregando o id do template VIVO da conta de agora. Avisar "outra conta" ali
 * e sugerir excluir mandaria o cliente apagar da Meta um modelo aprovado e em
 * uso. Exigindo NOT_FOUND, o aviso só aparece quando o registro é mesmo um
 * órfão sem contrapartida na conta atual.
 */
export function isOrphanFromAnotherWaba(record, currentWabaId) {
  if (!isFromAnotherWaba(record, currentWabaId)) return false
  return record.metaReviewStatus === 'NOT_FOUND'
}

// Status de revisão em que a Meta aceita EDIÇÃO do template (espelho do master).
const EDITABLE_REVIEW_STATUSES = ['APPROVED', 'REJECTED', 'PAUSED']

/**
 * Pode corrigir e reenviar (editar) na Meta? Exige que o template exista de fato
 * lá (metaTemplateId) e esteja em APPROVED/REJECTED/PAUSED — em análise (PENDING)
 * a Graph recusa, e registro que nunca chegou na Meta segue pelo reenviar
 * (canRetry), que cria. Aceita a linha mesclada da tabela (lê o .record) ou o
 * registro cru; linha "somente Meta" (sem registro local) não tem o que editar.
 */
export function canEdit(item) {
  if (!item) return false
  const record = item.record || item
  if (!record || !record._id || !record.metaTemplateId) return false
  if (record.localProcessingStatus === 'submitting') return false
  const status = String(record.metaReviewStatus || '').toUpperCase().trim()
  return EDITABLE_REVIEW_STATUSES.includes(status)
}

/**
 * Rótulo + cor (pt-br) do status de um item mesclado ou registro. O estado
 * local de processamento (enviando/falhou) tem prioridade sobre o status de
 * revisão da Meta.
 */
export function statusInfo(item) {
  const local = item?.localProcessingStatus
  if (local === 'submitting') return { label: 'Enviando para a Meta', color: STATUS_COLORS.blue }
  if (local === 'failed') return { label: 'Falha no envio', color: STATUS_COLORS.red }

  const status = String(item?.metaReviewStatus || item?.status || '').toUpperCase().trim()
  switch (status) {
    case 'APPROVED': return { label: 'Aprovado', color: STATUS_COLORS.green }
    case 'PENDING': return { label: 'Em análise', color: STATUS_COLORS.yellow }
    case 'IN_APPEAL': return { label: 'Em recurso', color: STATUS_COLORS.yellow }
    case 'REJECTED': return { label: 'Rejeitado', color: STATUS_COLORS.red }
    case 'PAUSED': return { label: 'Pausado', color: STATUS_COLORS.orange }
    case 'DISABLED': return { label: 'Desativado', color: STATUS_COLORS.gray }
    case 'FLAGGED': return { label: 'Sinalizado', color: STATUS_COLORS.red }
    case 'LIMIT_EXCEEDED': return { label: 'Limite excedido', color: STATUS_COLORS.red }
    case 'NOT_FOUND': return { label: 'Não encontrado na Meta', color: STATUS_COLORS.gray }
    default: return { label: 'Desconhecido', color: STATUS_COLORS.gray }
  }
}

// Números das variáveis {{n}} presentes no corpo, únicos e em ordem crescente.
export function extractBodyVariables(body) {
  const matches = String(body || '').match(/\{\{(\d+)\}\}/g) || []
  const numbers = matches.map((m) => parseInt(m.replace(/\D/g, ''), 10))
  return [...new Set(numbers)].sort((a, b) => a - b)
}

// ---------------------------------------------------------------------------
// Placeholders [[CHAVE]] dos modelos da biblioteca compartilhada
// ---------------------------------------------------------------------------
// A biblioteca de modelos é a MESMA para todos os provedores clientes da GLA.
// Por isso dados de marca (nome da empresa, link de atendimento) não podem estar
// cravados no template — senão um provedor instalaria a mensagem com a marca de
// outro. Eles entram como tokens [[CHAVE]] (colchetes duplos, pra não colidir
// com as variáveis {{n}} da Meta) e são resolvidos na instalação. As funções
// abaixo servem só pro PREVIEW local; a resolução que vale é a do backend.

// Casa colchetes duplos com chave em maiúsculas/dígitos/underscore (ex.: [[EMPRESA]]).
const PLACEHOLDER_TOKEN = /\[\[([A-Z0-9_]+)\]\]/g

// Substitui [[CHAVE]] pelo valor informado. Sem valor (ausente ou vazio) mantém
// o token intacto — assim hasUnresolvedPlaceholders continua enxergando o que
// falta preencher.
function substitutePlaceholderTokens(text, valores) {
  return text.replace(PLACEHOLDER_TOKEN, (token, key) => {
    const value = valores == null ? undefined : valores[key]
    if (value == null || String(value) === '') return token
    return String(value)
  })
}

// Aplica uma função a toda string do valor, em qualquer profundidade, devolvendo
// uma CÓPIA nova (não muta a entrada). Cobre text, url e os arrays de example
// (inclusive o body_text aninhado).
function deepMapStrings(value, fn) {
  if (typeof value === 'string') return fn(value)
  if (Array.isArray(value)) return value.map((item) => deepMapStrings(item, fn))
  if (value && typeof value === 'object') {
    const out = {}
    for (const k of Object.keys(value)) out[k] = deepMapStrings(value[k], fn)
    return out
  }
  return value
}

/**
 * Resolve os tokens [[CHAVE]] dos componentes com os valores informados e
 * devolve uma cópia nova. As variáveis {{n}} da Meta nunca são tocadas (o regex
 * só casa colchetes duplos). Usada apenas para montar o preview local.
 */
export function applyPlaceholders(components, valores = {}) {
  return deepMapStrings(components, (text) => substitutePlaceholderTokens(text, valores))
}

// true se ainda sobrou algum token [[...]] não resolvido em qualquer string.
export function hasUnresolvedPlaceholders(components) {
  let pending = false
  const scan = (value) => {
    if (pending) return
    if (typeof value === 'string') {
      if (/\[\[[A-Z0-9_]+\]\]/.test(value)) pending = true
      return
    }
    if (Array.isArray(value)) {
      value.forEach(scan)
      return
    }
    if (value && typeof value === 'object') Object.values(value).forEach(scan)
  }
  scan(components)
  return pending
}

/**
 * Valida os valores dos placeholders com as MESMAS regras do backend, pra pegar
 * o erro antes do round-trip: obrigatório em branco, maxLength estourado e link
 * sem https:// (chaves URL_*). Devolve { errors: { CHAVE: 'msg pt-br' } } —
 * objeto de erros vazio quando está tudo certo.
 */
export function validatePlaceholderValues(placeholders, valores = {}) {
  const errors = {}
  const list = Array.isArray(placeholders) ? placeholders : []

  for (const ph of list) {
    if (!ph || !ph.key) continue
    const key = ph.key
    const label = ph.label || 'Este campo'
    const raw = valores != null && valores[key] != null ? String(valores[key]) : ''
    const value = raw.trim()

    if (ph.required && !value) {
      errors[key] = `${label} é obrigatório.`
      continue
    }
    // Campo opcional em branco não tem mais o que validar.
    if (!value) continue

    if (ph.maxLength && value.length > ph.maxLength) {
      errors[key] = `Use no máximo ${ph.maxLength} caracteres (${value.length} agora).`
      continue
    }
    if (/^URL_/.test(key) && !/^https:\/\//i.test(value)) {
      errors[key] = 'O link precisa começar com https://.'
      continue
    }
  }

  return { errors }
}

/**
 * Une os placeholders de VÁRIOS modelos num único conjunto de campos, deduplicando
 * por chave. É o que sustenta o formulário compartilhado do dialog: a pessoa
 * digita "nome da empresa" e "link de atendimento" UMA vez e vale pra todos os
 * modelos selecionados.
 *
 * Regra do maxLength: quando a MESMA chave aparece em modelos diferentes com
 * limites diferentes (ex.: EMPRESA 39/41/40/37, porque o sufixo do cabeçalho
 * muda por modelo), o limite efetivo do campo compartilhado é o MENOR entre eles.
 * Senão um nome que cabe num modelo estouraria o header de outro e a instalação
 * falharia no meio da fila. Label/description/example ficam com os do primeiro
 * modelo que declarou a chave; `required` é verdadeiro se QUALQUER modelo exigir.
 *
 * Aceita tanto os itens da biblioteca (com `.placeholders`) quanto arrays de
 * specs direto. Itens sem placeholders (modelo antigo) simplesmente não entram.
 */
export function mergePlaceholderSpecs(items) {
  const list = Array.isArray(items) ? items : []
  const byKey = new Map()

  for (const entry of list) {
    const phs = Array.isArray(entry)
      ? entry
      : (entry && Array.isArray(entry.placeholders) ? entry.placeholders : [])

    for (const ph of phs) {
      if (!ph || !ph.key) continue
      const existing = byKey.get(ph.key)
      if (!existing) {
        // Primeira ocorrência da chave: guarda uma cópia com label/description/example dela.
        byKey.set(ph.key, { ...ph })
        continue
      }
      // Mesma chave em outro modelo: o limite efetivo é o MENOR entre os modelos.
      if (ph.maxLength != null) {
        existing.maxLength = existing.maxLength != null
          ? Math.min(existing.maxLength, ph.maxLength)
          : ph.maxLength
      }
      // Se qualquer modelo exige a chave, o campo compartilhado é obrigatório.
      if (ph.required) existing.required = true
    }
  }

  return [...byKey.values()]
}

/**
 * Recorta os valores compartilhados ({ EMPRESA, URL_ATENDIMENTO, ... }) só nas
 * chaves que UM modelo declara — é o que vai no corpo do install daquele item.
 * Devolve os valores já aparados (trim) e como string; chave sem valor vira ''.
 * `spec` é o array de placeholders do item (o mesmo que `item.placeholders`).
 */
export function pickPlaceholderValues(spec, valores = {}) {
  const list = Array.isArray(spec) ? spec : []
  const src = valores && typeof valores === 'object' ? valores : {}
  const out = {}
  for (const ph of list) {
    if (!ph || !ph.key) continue
    const raw = src[ph.key]
    out[ph.key] = raw == null ? '' : String(raw).trim()
  }
  return out
}

/**
 * Monta { name, language, category, components } no formato da Graph API a
 * partir do formulário do dialog "Criar template", validando o básico no
 * cliente antes de bater no backend. Retorna { template, errors } — template
 * é null quando há erros.
 *
 * form: { name, language, category, header, body, examples: [], footer,
 *         buttons: [{ type, text, url, urlExample }] } — urlExample só importa
 * quando a url do botão é dinâmica ({{n}}): vira o example exigido pela Meta.
 */
export function buildCustomTemplateDraft(form = {}) {
  const errors = []
  const name = String(form.name || '').trim()
  const language = String(form.language || '').trim() || IDIOMA_DO_ENVIO
  const category = (String(form.category || '').trim() || 'UTILITY').toUpperCase()
  const header = String(form.header || '').trim()
  const body = String(form.body || '').trim()
  const footer = String(form.footer || '').trim()
  const examples = Array.isArray(form.examples) ? form.examples : []
  const buttons = Array.isArray(form.buttons) ? form.buttons : []

  if (!name) {
    errors.push('Informe o nome do modelo.')
  } else if (!/^[a-z][a-z0-9_]*$/.test(name)) {
    errors.push('O nome deve estar em snake_case: só letras minúsculas, números e "_" (ex.: aviso_de_vencimento).')
  } else if (name.length > 512) {
    errors.push('O nome pode ter no máximo 512 caracteres.')
  }

  if (!VALID_CATEGORIES.includes(category)) {
    errors.push('Categoria inválida. Use Utilitário, Marketing ou Autenticação.')
  }

  if (header) {
    // Limite da Meta pro cabeçalho de texto (mesmo teto validado no master).
    if (header.length > 60) {
      errors.push('O cabeçalho pode ter no máximo 60 caracteres.')
    }
    // Cabeçalho com {{n}} exigiria example.header_text — o editor só suporta
    // cabeçalho FIXO; variável fica no corpo, onde os exemplos já são guiados.
    if (/\{\{\d+\}\}/.test(header)) {
      errors.push('O cabeçalho não aceita variáveis {{n}} — use um texto fixo (as variáveis vão no corpo).')
    }
  }

  if (!body) {
    errors.push('O corpo da mensagem é obrigatório.')
  } else if (body.length > 1024) {
    errors.push('O corpo pode ter no máximo 1.024 caracteres.')
  }

  const variables = extractBodyVariables(body)
  const sequential = variables.every((v, i) => v === i + 1)
  if (variables.length > 0 && !sequential) {
    errors.push('As variáveis do corpo devem ser sequenciais a partir de {{1}}, sem pular números.')
  }
  variables.forEach((v, i) => {
    const example = String(examples[i] == null ? '' : examples[i]).trim()
    if (!example) errors.push(`Informe um exemplo para a variável {{${v}}}.`)
  })

  if (footer && footer.length > 60) {
    errors.push('O rodapé pode ter no máximo 60 caracteres.')
  }

  const builtButtons = []
  buttons.forEach((b, idx) => {
    const type = String(b?.type || '').toUpperCase().trim()
    const text = String(b?.text || '').trim()
    const url = String(b?.url || '').trim()
    const urlExample = String(b?.urlExample || '').trim()

    // Linha de botão totalmente vazia é ignorada (o usuário adicionou e não preencheu)
    if (!text && !url) return

    if (!text) {
      errors.push(`Informe o texto do botão ${idx + 1}.`)
      return
    }
    if (text.length > 25) {
      errors.push(`O texto do botão ${idx + 1} pode ter no máximo 25 caracteres.`)
      return
    }

    if (type === 'URL') {
      // Só https: a Meta recusa http e o backend barra qualquer outro esquema.
      // Aceitar http aqui só adiantaria um erro vindo de longe, lá no install.
      if (!/^https:\/\/\S+$/i.test(url)) {
        errors.push(`Informe uma URL começando com https:// para o botão ${idx + 1}.`)
        return
      }
      if (/\{\{/.test(url)) {
        // URL dinâmica: a Meta exige example com a URL completa de amostra
        // (a variável {{n}} substituída por um valor real). Sem validar aqui,
        // o erro só apareceria vindo do master, depois do round-trip.
        if (!urlExample) {
          errors.push(`Informe a URL de exemplo do botão ${idx + 1}: a URL tem variável {{...}} e a Meta exige uma amostra da URL completa.`)
          return
        }
        if (!/^https:\/\/\S+$/i.test(urlExample)) {
          errors.push(`A URL de exemplo do botão ${idx + 1} precisa começar com https://.`)
          return
        }
        builtButtons.push({ type: 'URL', text, url, example: [urlExample] })
        return
      }
      // URL fixa não leva example — mesmo que tenha sobrado um urlExample de
      // quando o botão era dinâmico, ele fica de fora do payload.
      builtButtons.push({ type: 'URL', text, url })
    } else if (type === 'QUICK_REPLY') {
      builtButtons.push({ type: 'QUICK_REPLY', text })
    } else {
      errors.push(`Tipo de botão inválido no botão ${idx + 1}. Use URL ou resposta rápida.`)
    }
  })

  if (errors.length > 0) return { template: null, errors }

  const components = []
  // Ordem canônica da Graph: HEADER (quando houver), BODY, FOOTER, BUTTONS.
  if (header) components.push({ type: 'HEADER', format: 'TEXT', text: header })
  components.push({
    type: 'BODY',
    text: body,
    ...(variables.length > 0
      ? { example: { body_text: [variables.map((v, i) => String(examples[i]).trim())] } }
      : {}),
  })
  if (footer) components.push({ type: 'FOOTER', text: footer })
  if (builtButtons.length > 0) components.push({ type: 'BUTTONS', buttons: builtButtons })

  return { template: { name, language, category, components }, errors: [] }
}

// ---------------------------------------------------------------------------
// Do template aprovado ao disparo pronto (snippet do ERP)
// ---------------------------------------------------------------------------
// O ERP dispara um template Meta mandando uma STRING de metatags no campo
// "message" de /messages/sendMessage. Quem faz o parse dessa string é o
// secondary (getEvolutionApiWhatsappCloudApiMetaTag em utils/metaTag.js). As
// funções abaixo montam essa string a partir de um template já aprovado — e a
// tela "Como usar" mostra ela pro cliente copiar/testar.

// Token que o secondary detecta (PAYMENT_LINK_CODES) pra injetar o link de
// pagamento hiperpay no botão dinâmico. Precisa bater byte a byte com o que
// sendEvolutionWhatsappBusinessMessageTemplate procura no secondary.
export const PAYMENT_LINK_CODE_TOKEN = '{{PAYMENT_LINK_CODE}}'

function findComponentByType(components, type) {
  const list = Array.isArray(components) ? components : []
  return list.find((c) => String(c?.type || '').toUpperCase().trim() === type) || null
}

// Linha de exemplos do corpo (example.body_text[0]) alinhada às variáveis {{n}}.
function bodyExampleRow(bodyComponent) {
  const bt = bodyComponent?.example?.body_text
  return Array.isArray(bt) && Array.isArray(bt[0]) ? bt[0] : []
}

// URL completa de amostra de um botão de URL dinâmica. Na Graph o example do
// botão é um ARRAY de strings (a biblioteca manda ['https://...']); aceita
// também string solta por tolerância. Sem example, devolve ''.
function buttonUrlExample(button) {
  const example = button?.example
  if (Array.isArray(example)) return example[0] != null ? String(example[0]) : ''
  if (typeof example === 'string') return example
  return ''
}

// Um botão de URL é "dinâmico de pagamento" quando aponta pro hiperpay E carrega
// uma variável {{...}} (o link por fatura que a Meta resolve no envio). É esse
// botão que recebe o token {{PAYMENT_LINK_CODE}} no snippet — o secondary troca
// pelo link real na hora do disparo.
//
// Ter só {{...}} NÃO basta: uma URL dinâmica de rastreio
// ('https://track.exemplo.com/{{1}}') também tem {{n}} e não é pagamento —
// marcá-la como pagamento emitiria {{PAYMENT_LINK_CODE}} no lugar da URL real, e
// o secondary trataria como botão hiperpay (descartaria o parâmetro, fazendo a
// Meta rejeitar, ou injetaria um link de pagamento numa URL de rastreio). O sinal
// que vale é o host hiperpay. Botão de URL fixa (atendimento) entra com a própria url.
function isDynamicPaymentButton(button) {
  const url = String(button?.url || '')
  return /hiperpay/i.test(url) && /\{\{.*?\}\}/.test(url)
}

/**
 * Monta a STRING de disparo (metatags) de um template aprovado, no formato EXATO
 * que o parser do secondary espera:
 *
 *   %WhatsappCloudApi%%Template%%Name%NOME%/Name%
 *     %VariableNumeric%v1%/VariableNumeric%...   (um por variável {{n}} do corpo)
 *     %ButtonUrl%conteudo%/ButtonUrl%...          (um por botão de URL, na ordem)
 *   %/Template%%/WhatsappCloudApi%
 *
 * - Uma variável {{n}} do BODY vira um %VariableNumeric%, na ordem 1..N. O valor
 *   é um exemplo legível: o example.body_text da variável quando houver, senão
 *   "valor_da_variavel_N" (nunca vazio — o parser trocaria vazio pela mensagem
 *   padrão da instância).
 * - Cada botão de URL vira um %ButtonUrl%, NA ORDEM POSICIONAL (o secondary
 *   percorre por índice). Botão dinâmico de pagamento recebe {{PAYMENT_LINK_CODE}};
 *   botão de URL fixa recebe a própria url (o secondary o pula, mas a posição
 *   precisa existir). QUICK_REPLY não vira %ButtonUrl%.
 */
export function buildSendSnippet(item) {
  const components = Array.isArray(item?.components) ? item.components : []
  const name = String(item?.name || item?.metaTemplateName || '').trim()

  const body = findComponentByType(components, 'BODY')
  const variables = extractBodyVariables(body?.text)
  const exampleRow = bodyExampleRow(body)

  // Uma tag por linha. O parser do secondary limpa as quebras de cada valor
  // antes do trim, então a quebra aqui é puramente visual — e ela importa: em
  // linha única o cliente não enxerga onde termina uma variável e começa a
  // outra, e acaba editando a coisa errada ao pôr os dados dele no lugar dos
  // exemplos.
  const linhas = ['%WhatsappCloudApi%', '%Template%', `%Name%${name}%/Name%`]

  variables.forEach((n, i) => {
    const ex = exampleRow[i]
    const value = ex != null && String(ex).trim() !== ''
      ? String(ex).trim()
      : `valor_da_variavel_${n}`
    linhas.push(`%VariableNumeric%${value}%/VariableNumeric%`)
  })

  const buttonsComponent = findComponentByType(components, 'BUTTONS')
  const buttons = Array.isArray(buttonsComponent?.buttons) ? buttonsComponent.buttons : []
  for (const button of buttons) {
    const type = String(button?.type || '').toUpperCase().trim()
    // QUICK_REPLY (e qualquer não-URL) não entra na lista de buttonsUrl.
    if (type !== 'URL') continue
    const content = isDynamicPaymentButton(button)
      ? PAYMENT_LINK_CODE_TOKEN
      : String(button?.url || '').trim()
    linhas.push(`%ButtonUrl%${content}%/ButtonUrl%`)
  }

  linhas.push('%/Template%', '%/WhatsappCloudApi%')
  return linhas.join('\n')
}

// Rótulo curto pra uma variável {{n}}, inferido do texto imediatamente antes
// dela no corpo (as últimas palavras do mesmo trecho, sem cruzar fim de frase
// nem a variável anterior). Sem contexto útil, cai em "Variável N".
function inferVariableHint(text, n) {
  const token = `{{${n}}}`
  const idx = String(text).indexOf(token)
  if (idx < 0) return `Variável ${n}`

  let before = String(text).slice(0, idx)
  // Não cruza fim de frase, quebra de linha, nem a variável {{...}} anterior.
  const cut = Math.max(
    before.lastIndexOf('.'),
    before.lastIndexOf('!'),
    before.lastIndexOf('?'),
    before.lastIndexOf('\n'),
    before.lastIndexOf('}}')
  )
  if (cut >= 0) before = before.slice(cut + 1)

  const clean = before
    .replace(/[*_~`]/g, ' ')        // marcadores de markdown do WhatsApp
    .replace(/[#—–\-:,;(){}]/g, ' ') // separadores/pontuação colados na variável
    .replace(/\s+/g, ' ')
    .trim()
  if (!clean) return `Variável ${n}`

  const words = clean.split(' ').filter(Boolean)
  return words.slice(-3).join(' ') || `Variável ${n}`
}

/**
 * Mapeia cada variável {{n}} do corpo para um rótulo curto do que o ERP põe
 * naquela posição. Devolve [{ n, hint }] na ordem do corpo. Serve pra tabela da
 * tela "Como usar" explicar cada %VariableNumeric% do snippet.
 */
export function describeSnippetVariables(item) {
  const components = Array.isArray(item?.components) ? item.components : []
  const body = findComponentByType(components, 'BODY')
  const text = String(body?.text || '')
  return extractBodyVariables(text).map((n) => ({ n, hint: inferVariableHint(text, n) }))
}

/**
 * Inverso do que o preview do CreateTemplateDialog faz (form -> components):
 * recebe os components JÁ RESOLVIDOS (sem [[...]]) e devolve o form
 * { name, language, category, body, examples, footer,
 *   buttons:[{type,text,url,urlExample}] } pronto pra semear o editor. Serve
 * pra "Personalizar modelo": o cliente abre um recomendado já preenchido e
 * ajusta (tirar botão, deixar texto puro).
 *
 * O HEADER de TEXTO vem junto (campo `header` do form) — sem isso, personalizar
 * ou corrigir um modelo perdia o "[[EMPRESA]] - Sufixo" da marca silenciosamente.
 * Cabeçalho de MÍDIA (IMAGE/DOCUMENT/VIDEO) continua fora: o editor só conhece
 * texto, então ele sai como '' sem quebrar o form.
 *
 * name/language/category não vivem nos components — vêm por `meta` (o chamador
 * passa os dados do modelo). Sem `meta`, caem nos defaults do editor.
 */
export function componentsToForm(components, meta = {}) {
  const list = Array.isArray(components) ? components : []
  const headerComponent = findComponentByType(list, 'HEADER')
  const body = findComponentByType(list, 'BODY')
  const footer = findComponentByType(list, 'FOOTER')
  const buttonsComponent = findComponentByType(list, 'BUTTONS')

  // Só cabeçalho de TEXTO tem lugar no editor; formato ausente conta como texto
  // (a Graph às vezes omite format em header textual).
  const headerFormat = String(headerComponent?.format || 'TEXT').toUpperCase().trim()
  const header = headerFormat === 'TEXT' ? String(headerComponent?.text || '') : ''

  const bodyText = String(body?.text || '')
  const variables = extractBodyVariables(bodyText)
  const exampleRow = bodyExampleRow(body)
  // examples na ORDEM das variáveis do corpo.
  const examples = variables.map((v, i) => (exampleRow[i] != null ? String(exampleRow[i]) : ''))

  const buttons = Array.isArray(buttonsComponent?.buttons)
    ? buttonsComponent.buttons.map((b) => {
      const isUrl = String(b?.type || '').toUpperCase().trim() === 'URL'
      return {
        // O editor só conhece URL e resposta rápida; qualquer outro tipo (raro
        // na biblioteca) vira resposta rápida pra caber no form.
        type: isUrl ? 'URL' : 'QUICK_REPLY',
        text: String(b?.text || ''),
        url: isUrl ? String(b?.url || '') : '',
        // URL dinâmica ({{n}} na url) carrega example com a URL completa de
        // amostra — na Graph é um array; no form vira o campo "URL de exemplo".
        // Descartá-lo aqui deixava o Personalizar num beco sem saída: a
        // validação exige o example e a tela não tinha onde preenchê-lo.
        urlExample: isUrl ? buttonUrlExample(b) : '',
      }
    })
    : []

  return {
    name: meta.name != null ? String(meta.name) : '',
    language: meta.language != null ? String(meta.language) : IDIOMA_DO_ENVIO,
    category: meta.category != null ? String(meta.category) : 'UTILITY',
    header,
    body: bodyText,
    examples,
    footer: String(footer?.text || ''),
    buttons,
  }
}

/**
 * Mensagem pt-br de um erro axios das rotas de metaTemplates. Usa a `message`
 * do envelope de domínio ({ success:false, code, message }); pra
 * META_TEMPLATE_GRAPH_ERROR traduz o metaError via metaErrors.js — mas só
 * quando a tradução reconheceu o erro de fato (a entrada genérica fala de
 * "envio de mensagem" e jogaria fora a message específica do master).
 * Erros sem envelope (rede, rota inexistente) também saem em pt-br: o axios
 * só tem mensagens em inglês e elas nunca devem chegar cruas no cliente.
 * Nunca expõe token/credenciais — só o que o backend mandou no envelope.
 */
export function describeApiError(error) {
  const data = error?.response?.data
  if (data?.code === 'META_TEMPLATE_GRAPH_ERROR' && data?.metaError) {
    const translated = translateMetaError(data.metaError)
    if (translated && !translated.generica) {
      const complement = translated.acao || translated.explicacao || ''
      return complement ? `${translated.titulo}. ${complement}` : translated.titulo
    }
    if (data.message) {
      // metaError não mapeado: a message pt-br do master é mais específica que
      // o fallback genérico. Anexa o detalhe técnico da Meta, porque não
      // existe "ver detalhe" nas snackbars/dialogs deste módulo.
      const tecnico = translated?.tecnico
      return tecnico && !String(data.message).includes(tecnico)
        ? `${data.message} (detalhe técnico: ${tecnico})`
        : data.message
    }
  }
  if (data?.message) return data.message

  // Daqui pra baixo não há envelope de domínio — só o erro cru do axios.
  if (error?.response?.status) {
    // Resposta HTTP sem envelope: ex. painel novo publicado antes de o master
    // ganhar as rotas de templates (404 cru de rota inexistente).
    return `O servidor respondeu de forma inesperada (HTTP ${error.response.status}). Tente novamente em instantes; se persistir, contate o suporte.`
  }
  if (/network error|timeout/i.test(error?.message || '')) {
    return 'Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.'
  }
  if (error?.message) {
    return `Erro inesperado ao falar com o servidor. (detalhe técnico: ${error.message})`
  }
  return 'Erro inesperado ao falar com o servidor.'
}

const metaTemplatesLib = {
  META_OFFICIAL_PROVIDER,
  STATUS_COLORS,
  TEMPLATE_CATEGORIES,
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_CATEGORY_SHORT,
  isMetaOfficialProvider,
  mergeTemplates,
  canRetry,
  canEdit,
  isFromAnotherWaba,
  isOrphanFromAnotherWaba,
  statusInfo,
  extractBodyVariables,
  applyPlaceholders,
  hasUnresolvedPlaceholders,
  validatePlaceholderValues,
  mergePlaceholderSpecs,
  pickPlaceholderValues,
  buildCustomTemplateDraft,
  PAYMENT_LINK_CODE_TOKEN,
  buildSendSnippet,
  describeSnippetVariables,
  componentsToForm,
  describeApiError,
}

export default metaTemplatesLib
